import { useUtility } from "./useUtility";

import { Tag } from "@/api/models/Person/Tag";
import { tagMapper } from "@/api/mappers/TagMapper";
import tagData from "@/api/mock/tags.json"; // temporary mock data

import { MembershipType } from "@/api/models/MembershipType";
import { membershipTypeMapper } from "@/api/mappers/MembershipTypeMapper";
import membshipTypeData from "@/api/mock/membershipTypes.json"; // temporary mock data

import { ProgramType } from "@/api/models/ProgramType";
import { programTypeMapper } from "@/api/mappers/ProgramTypeMapper";
import programTypesData from "@/api/mock/programTypes.json"; // temporary mock data

import { MembershipTypeDiscount } from "@/api/models/MembershipTypeDiscount";
import { membershipTypeDiscountMapper } from "@/api/mappers/MembershipTypeDiscountMapper";
import membershipTypeDiscountData from "@/api/mock/membershipTypeDiscounts.json"; // temporary mock data

import { Rating } from "@/api/models/Rating";
import { ratingMapper } from "@/api/mappers/RatingMapper";
import ratingData from "@/api/mock/ratings.json"; // temporary mock data

import { RatingProgramMap } from "@/api/models/RatingProgramMap";
import { ratingsProgramsMapper } from "@/api/mappers/RatingsProgramsMapper";
import ratingsProgramsData from "@/api/mock/ratingsPrograms.json";

import { BoatType } from "@/api/models/BoatType";
import { boatMapper } from "@/api/mappers/BoatMapper";
import boatData from "@/api/mock/boatTypes.json"; // temporary mock data

import { PersonType } from "@/api/models/Person/PersonType";
import { personTypeMapper } from "@/api/mappers/PersonTypeMapper";
import personTypeData from "@/api/mock/personTypes.json"; // temporary mock data

import { Country } from "@/api/models/Country";
import { countryMapper } from "@/api/mappers/CountryMapper";
import countryData from "@/api/mock/countries.json"; // temporary mock data

import { AmericanState } from "@/api/models/AmericanState";
import { americanStateMapper } from "@/api/mappers/AmericanStateMapper";
import usStateData from "@/api/mock/states.json"; // temporary mock data

import { SwimProof } from "@/api/models/SwimProof";
import { swimProofMapper } from "@/api/mappers/SwimProofMapper";
import swimProofData from "@/api/mock/swimProofMethods.json"; // temporary mock data

import { DonationFund } from "@/api/models/DonationFunds";
import { donationFundsMapper } from "@/api/mappers/DonationFundsMapper";
import donationFundsData from "@/api/mock/donationFunds.json"; // temporary mock data

import { DonationInitiative } from "@/api/models/DonationInitiatives";
import { donationInitiativesMapper } from "@/api/mappers/DonationInitiativesMapper";
import donationInitiativesData from "@/api/mock/donationInitiatives.json"; // temporary mock data

import { ApClassType } from "@/api/models/Classes/ApClassType";
import { apClassTypesMapper } from "@/api/mappers/ApClassTypeMapper";
import apClassTypesData from "@/api/mock/apClassTypes.json"; // temporary mock data

import { ClassLocation } from "@/api/models/Classes/ClassLocation";
import { classLocationMapper } from "@/api/mappers/ClassLocationMapper";
import classLocationData from "@/api/mock/classLocations.json";

import { JpClassType } from "@/api/models/Classes/JpClassType";
import { jpClassTypeMapper } from "@/api/mappers/JpClassTypeMapper";
import jpClassTypeData from "@/api/mock/jpClassTypes.json";

import signupTypeData from "@/api/mock/signupTypes.json";
import signOutTypeData from "@/api/mock/signOutTypes.json";

import { ClassInstructor } from "@/api/models/Classes/ClassInstructor";
import { classInstructorMapper } from "@/api/models/Classes/ClassInstructor";
import classInstructorData from "@/api/mock/classInstructors.json";

export type LookupTable = Record<string, string>;

export interface AppData {
  [key: string]: any;
  readonly personTypes: PersonType[];
  readonly availableTags: Tag[];
  readonly membershipTypes: MembershipType[];
  readonly membershipTypeDiscounts: MembershipTypeDiscount[];
  readonly programTypes: ProgramType[];
  readonly ratings: Rating[];
  readonly ratingProgramMaps: RatingProgramMap[];
  readonly boatTypes: BoatType[];
  readonly countries: Country[];
  readonly americanStates: AmericanState[];
  readonly swimProofMethods: SwimProof[];
  readonly signupTypes: LookupTable;
  readonly signOutTypes: LookupTable;
  readonly donationFunds: DonationFund[];
  readonly donationInitiatives: DonationInitiative[];
  readonly apClassTypes: ApClassType[];
  readonly classLocations: ClassLocation[];
  readonly classInstructors: ClassInstructor[];
  readonly jpClassTypes: JpClassType[];
  findPersonType: (id: number) => PersonType | null;
  findTag: (id: number) => Tag | null;
  findMembershipType: (id: number) => MembershipType | null;
  findMembershipDiscountType: (id: number) => MembershipTypeDiscount | null;
  findProgramType: (id: number) => ProgramType | null;
  findRating: (id: number) => Rating | null;
  findBoatType: (id: number) => BoatType | null;
  findDonationFund: (id: number) => DonationFund | null;
  findDonationInitiative: (id: number) => DonationInitiative | null;
  findSignupType: (token: string) => string | undefined;
  findSignOutType: (token: string) => string | undefined;
  findClassLocation: (id: number) => ClassLocation | undefined;
}

// Get utility functions
const { sortByName } = useUtility();

/**
 * Factory to create a find function for data by id,
 * "T" is inferred by the type of array you pass in, must have an "id" property on each item
 * @param data - Array of data
 * @returns Find function
 */
function createFindDataById<T extends { id: number | null }>(data: T[]) {
  return function (id: number): T | null {
    return data.find((item: T) => item.id === id) || null;
  };
}

// Cached app data
let appData: AppData | null = null;

/**
 * Cached composable used for app-wide data
 * @todo Replace mock with API calls
 */
export const useAppData = (): AppData => {
  if (!appData) {
    // 1. Map DTOs to models
    // 2. Create a find function for the resulting arrays

    const personTypes = personTypeData.map(personTypeMapper);
    const findPersonType = createFindDataById(personTypes);

    const availableTags = tagData
      .map((dto) => tagMapper({ ...dto, tagName: dto.tagName ?? "" }))
      .sort(sortByName);
    const findTag = createFindDataById(availableTags);

    const membershipTypes = membshipTypeData
      .map(membershipTypeMapper)
      .filter((mt): mt is MembershipType => mt.id !== null)
      .sort(sortByName);
    const findMembershipType = createFindDataById(membershipTypes);

    const membershipTypeDiscounts = membershipTypeDiscountData.map(
      membershipTypeDiscountMapper,
    );
    const findMembershipDiscountType = createFindDataById(
      membershipTypeDiscounts,
    );

    const programTypes = programTypesData
      .map(programTypeMapper)
      .sort(sortByName);
    const findProgramType = createFindDataById(programTypes);

    const ratings = ratingData.map(ratingMapper);
    const findRating = createFindDataById(ratings);

    const boatTypes = boatData.map(boatMapper);
    const findBoatType = createFindDataById(boatTypes);

    const donationFunds = donationFundsData.map(donationFundsMapper);
    const findDonationFund = createFindDataById(donationFunds);

    const donationInitiatives = donationInitiativesData.map(
      donationInitiativesMapper,
    );
    const findDonationInitiative = createFindDataById(donationInitiatives);

    const countries = countryData.map(countryMapper);
    const americanStates = usStateData.map(americanStateMapper);
    const swimProofMethods = swimProofData.map(swimProofMapper);

    const signupTypes = signupTypeData as LookupTable;
    const findSignupType = (token: string): string | undefined =>
      signupTypes[token];

    const signOutTypes = signOutTypeData as LookupTable;
    const findSignOutType = (token: string): string | undefined =>
      signOutTypes[token];

    const ratingProgramMaps = ratingsProgramsData.map(ratingsProgramsMapper);

    const apClassTypes = apClassTypesData.map(apClassTypesMapper);

    const classLocations = classLocationData.map(classLocationMapper);
    const findClassLocation = (id: number) =>
      classLocations.find((location) => location.id === id);

    const classInstructors = classInstructorData.map(classInstructorMapper);
    const jpClassTypes = jpClassTypeData.map(jpClassTypeMapper);

    // Cache the data
    appData = {
      personTypes,
      findPersonType,
      availableTags,
      findTag,
      membershipTypes,
      findMembershipType,
      membershipTypeDiscounts,
      findMembershipDiscountType,
      programTypes,
      findProgramType,
      ratings,
      findRating,
      boatTypes,
      findBoatType,
      signupTypes,
      findSignupType,
      signOutTypes,
      findSignOutType,
      donationFunds,
      findDonationFund,
      countries,
      americanStates,
      swimProofMethods,
      ratingProgramMaps,
      apClassTypes,
      classLocations,
      findClassLocation,
      donationInitiatives,
      findDonationInitiative,
      classInstructors,
      jpClassTypes,
    };
  }

  return appData;
};
